import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import type { CourseAccess, Enrollment, PageMeta } from '@/lib/types'

export function useEnrollments() {
  return useQuery({
    queryKey: ['enrollments'],
    queryFn: () => api.get<{ enrollments: Enrollment[]; meta: PageMeta }>('/enrollments', {
      query: { per_page: 50 },
    }),
  })
}

export function useCourseAccess(enrollmentId: string) {
  return useQuery({
    queryKey: ['enrollment', enrollmentId],
    queryFn: () => api.get<CourseAccess>(`/enrollments/${enrollmentId}`),
  })
}

/**
 * Pendaftaran pemanggil pada sebuah kelas.
 *
 * Belum terdaftar bukan galat: API mengembalikan null, dan halaman kelas
 * memakainya untuk memilih antara tombol "Beli" dan "Lanjut belajar".
 */
export function useMyEnrollment(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['my-enrollment', slug],
    queryFn: async () => {
      try {
        const data = await api.get<{ enrollment: Enrollment | null }>(`/courses/${slug}/my-enrollment`)
        return data.enrollment
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null
        throw error
      }
    },
    enabled,
  })
}

export function useEnrollFree() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (courseId: string) => api.post<{ enrollment: Enrollment }>('/enrollments', { course_id: courseId }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['enrollments'] })
      client.invalidateQueries({ queryKey: ['my-enrollment'] })
    },
  })
}

export interface ProgressResult {
  enrollment_id: string
  progress_percent: number
  course_completed: boolean
}

export function useSaveProgress(enrollmentId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { lessonId: string; seconds_watched: number; completed: boolean }) =>
      api.post<ProgressResult>(`/lessons/${input.lessonId}/progress`, {
        seconds_watched: input.seconds_watched,
        completed: input.completed,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['enrollment', enrollmentId] })
      client.invalidateQueries({ queryKey: ['enrollments'] })
      client.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useSubmitReview(slug: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { rating: number; comment: string }) =>
      api.post<unknown>(`/courses/${slug}/reviews`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['course-reviews', slug] })
      client.invalidateQueries({ queryKey: ['course', slug] })
    },
  })
}
